import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { CLIENT_ID_HEADER, SELECTED_CLIENT_STORAGE_KEY } from '@/config/auth0.config'

// Base API URL - uses environment variable with fallback
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001'

function getSelectedClientHeader(): Record<string, string> {
  const selected = localStorage.getItem(SELECTED_CLIENT_STORAGE_KEY)
  if (!selected) return {}
  return { [CLIENT_ID_HEADER]: selected }
}

export type UnauthorizedEvent = 'refresh' | 'give-up'

class ApiClient {
  private client: AxiosInstance
  private getAccessToken: (() => Promise<string>) | null = null
  private onUnauthorized: ((event: UnauthorizedEvent) => void | Promise<void>) | null = null
  private unauthorizedInFlight: Promise<void> | null = null
  private refreshConsumed = false
  private sessionDead = false

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.client.interceptors.request.use(
      async (config) => {
        if (!this.getAccessToken) {
          return Promise.reject(new Error('Request blocked: access token not ready'))
        }

        const token = await this.getAccessToken()
        if (!token) {
          return Promise.reject(new Error('Request blocked: empty access token'))
        }

        config.headers.Authorization = `Bearer ${token}`
        Object.assign(config.headers, getSelectedClientHeader())
        return config
      },
      (error) => Promise.reject(error)
    )

    this.client.interceptors.response.use(
      (response) => {
        this.refreshConsumed = false
        return response
      },
      async (error) => {
        const original = error.config as (InternalAxiosRequestConfig & { _authRetry?: boolean }) | undefined
        if (error.response?.status === 401 && original && !original._authRetry) {
          original._authRetry = true
          await this.handleUnauthorized()
          if (!this.sessionDead) {
            return this.client.request(original)
          }
        }
        return Promise.reject(error)
      }
    )
  }

  setAuth0TokenGetter(getAccessToken: (() => Promise<string>) | null) {
    this.getAccessToken = getAccessToken
    this.unauthorizedInFlight = null
    this.refreshConsumed = false
    this.sessionDead = false
  }

  setUnauthorizedHandler(handler: ((event: UnauthorizedEvent) => void | Promise<void>) | null) {
    this.onUnauthorized = handler
  }

  private handleUnauthorized(): Promise<void> {
    if (this.sessionDead) return Promise.resolve()
    if (this.unauthorizedInFlight) return this.unauthorizedInFlight

    const handler = this.onUnauthorized
    if (!handler) return Promise.resolve()

    if (this.refreshConsumed) {
      this.sessionDead = true
      return Promise.resolve(handler('give-up'))
    }

    this.unauthorizedInFlight = Promise.resolve(handler('refresh'))
      .then(() => {
        this.refreshConsumed = true
      })
      .catch(() => {
        this.sessionDead = true
      })
      .finally(() => {
        this.unauthorizedInFlight = null
      })
    return this.unauthorizedInFlight
  }

  // Generic methods
  async get<T>(url: string, params?: any, config?: { timeout?: number }): Promise<T> {
    const response = await this.client.get<T>(url, { params, ...config })
    return response.data
  }

  async post<T>(url: string, data?: any, config?: { timeout?: number; signal?: AbortSignal }): Promise<T> {
    const response = await this.client.post<T>(url, data, config)
    return response.data
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.put<T>(url, data)
    return response.data
  }

  async patch<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.patch<T>(url, data)
    return response.data
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url)
    return response.data
  }
}

export const apiClient = new ApiClient()
